import { useMemo } from 'react'
import {
  asNumber,
  asRecordArray,
  asString,
  formatMonthLabel,
  formatPct,
  formatUsd,
  getSectionData,
} from '@/components/gcp/gcpHelpers'
import type {
  GcpAnalysisResult,
  LevelShiftRow,
  NewSkuRow,
  SpendBridge,
  SpendBridgePartition,
  WhatChangedPayload,
} from '@/services/gcpCosts'

interface GcpWhatChangedProps {
  analysis: GcpAnalysisResult
}

const PARTITION_STYLES: Record<SpendBridgePartition, string> = {
  new_sku: 'bg-purple-100 text-purple-800',
  grown: 'bg-red-100 text-red-800',
  shrunk: 'bg-green-100 text-green-800',
  disappeared: 'bg-gray-100 text-gray-700',
}

function parseWhatChanged(section: Record<string, unknown> | null): WhatChangedPayload {
  const raw = section?.what_changed
  if (!raw || typeof raw !== 'object') {
    return { new_skus: [], level_shifts: [] }
  }
  const payload = raw as Record<string, unknown>
  const newSkus: NewSkuRow[] = asRecordArray(payload.new_skus).map((row) => ({
    service: asString(row.service),
    sku: asString(row.sku),
    first_seen_month: asString(row.first_seen_month),
    monthly_cost_usd: asNumber(row.monthly_cost_usd) ?? 0,
  }))
  const levelShifts: LevelShiftRow[] = asRecordArray(payload.level_shifts).map((row) => ({
    service: asString(row.service),
    onset_date: asString(row.onset_date),
    recent_avg_daily_usd: asNumber(row.recent_avg_daily_usd) ?? 0,
    prior_avg_daily_usd: asNumber(row.prior_avg_daily_usd) ?? 0,
    ratio: asNumber(row.ratio) ?? 0,
    delta_daily_usd: asNumber(row.delta_daily_usd) ?? 0,
    run_rate_delta_monthly_usd: asNumber(row.run_rate_delta_monthly_usd) ?? 0,
  }))
  return { new_skus: newSkus, level_shifts: levelShifts }
}

function parseSpendBridge(section: Record<string, unknown> | null): SpendBridge | null {
  const raw = section?.spend_bridge
  if (!raw || typeof raw !== 'object') return null
  const bridge = raw as Record<string, unknown>
  const rows = asRecordArray(bridge.rows).map((row) => ({
    service: asString(row.service),
    prev_month_usd: asNumber(row.prev_month_usd) ?? 0,
    current_run_rate_usd: asNumber(row.current_run_rate_usd) ?? 0,
    delta_usd: asNumber(row.delta_usd) ?? 0,
    partition: asString(row.partition) as SpendBridgePartition,
    onset_date: row.onset_date ? asString(row.onset_date) : null,
  }))
  return {
    prev_complete_month: bridge.prev_complete_month
      ? asString(bridge.prev_complete_month)
      : null,
    prev_total_usd: asNumber(bridge.prev_total_usd) ?? 0,
    current_run_rate_total_usd: asNumber(bridge.current_run_rate_total_usd) ?? 0,
    total_delta_usd: asNumber(bridge.total_delta_usd) ?? 0,
    rows,
  }
}

export default function GcpWhatChanged({ analysis }: GcpWhatChangedProps) {
  const billingSection = getSectionData(analysis, 'billing')
  const whatChanged = useMemo(() => parseWhatChanged(billingSection), [billingSection])
  const bridge = useMemo(() => parseSpendBridge(billingSection), [billingSection])

  const hasContent =
    (bridge?.rows.length ?? 0) > 0 ||
    whatChanged.new_skus.length > 0 ||
    whatChanged.level_shifts.length > 0

  if (!billingSection || !hasContent) {
    return null
  }

  return (
    <div id="gcp-what-changed" className="space-y-4">
      <div>
        <h3 className="container-title">What changed</h3>
        <p className="text-sm text-gray-600">
          Prior complete month vs current run rate (trailing 14-day average, excluding billing
          export lag).
        </p>
      </div>

      {bridge ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-4 text-sm text-gray-700">
            <span>
              {formatMonthLabel(bridge.prev_complete_month ?? 'unknown')}:{' '}
              <span className="font-medium">{formatUsd(bridge.prev_total_usd)}</span>
            </span>
            <span>
              Current run rate:{' '}
              <span className="font-medium">{formatUsd(bridge.current_run_rate_total_usd)}/mo</span>
            </span>
            <span>
              Net change:{' '}
              <span className="font-medium">
                {bridge.total_delta_usd >= 0 ? '+' : ''}
                {formatUsd(bridge.total_delta_usd)}/mo
              </span>
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Prior month</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Run rate</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Δ / mo</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Onset</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bridge.rows.map((row) => (
                  <tr key={row.service}>
                    <td className="px-3 py-2 font-medium text-gray-900">{row.service}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(row.prev_month_usd)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(row.current_run_rate_usd)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {row.delta_usd >= 0 ? '+' : ''}
                      {formatUsd(row.delta_usd)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PARTITION_STYLES[row.partition] ?? 'bg-gray-100 text-gray-700'}`}
                      >
                        {row.partition.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{row.onset_date ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {whatChanged.new_skus.length > 0 ? (
        <div>
          <h4 className="mb-2 text-sm font-medium text-gray-800">New SKUs</h4>
          <div className="flex flex-wrap gap-2">
            {whatChanged.new_skus.map((sku) => (
              <span
                key={`${sku.service}::${sku.sku}`}
                className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-3 py-1 text-xs text-purple-900 ring-1 ring-purple-200"
              >
                <span className="font-medium">{sku.service}</span>
                <span className="text-purple-700">{sku.sku}</span>
                <span className="text-purple-600">
                  {formatUsd(sku.monthly_cost_usd)} · {formatMonthLabel(sku.first_seen_month)}
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {whatChanged.level_shifts.length > 0 ? (
        <div>
          <h4 className="mb-2 text-sm font-medium text-gray-800">Level shifts</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Onset</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Prior $/day</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Recent $/day</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Ratio</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Δ / mo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {whatChanged.level_shifts.map((shift) => (
                  <tr key={shift.service}>
                    <td className="px-3 py-2 font-medium text-gray-900">{shift.service}</td>
                    <td className="px-3 py-2 text-gray-800">{shift.onset_date}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(shift.prior_avg_daily_usd)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(shift.recent_avg_daily_usd)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatPct((shift.ratio - 1) * 100)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {shift.run_rate_delta_monthly_usd >= 0 ? '+' : ''}
                      {formatUsd(shift.run_rate_delta_monthly_usd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  )
}
